import { useEffect } from 'react';
import { substituteTags, capitalizeFirstWord } from '@peles/shared';
import type { Timeline, TimelineSection as TSection, TimelineEntry, EventTag } from '@peles/shared';

/** Capitalize the first word of a name, then resolve `{TAG}` placeholders. */
function formatName(text: string | null | undefined, tags: EventTag[]): string {
  return substituteTags(capitalizeFirstWord(text), tags);
}

interface TimelinePrintViewProps {
  timeline: Timeline;
  eventName: string;
  eventTags: EventTag[];
  appLogoUrl?: string | null;
  eventLogoUrl?: string | null;
  onClose: () => void;
}

function formatTime(time: string | null | undefined): string {
  if (!time) return '';
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const h = parseInt(match[1]);
    const m = match[2];
    return `${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  return time;
}

function formatDisplayDate(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '';
  // Parse as local date to avoid UTC-to-local timezone shift on ISO strings like "2026-05-26T00:00:00.000Z"
  const str = typeof dateStr === 'string' ? dateStr : dateStr.toISOString();
  const datePart = str.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function buildPrintHtml(
  timeline: Timeline,
  sections: TSection[],
  eventName: string,
  eventTags: EventTag[],
  appLogoUrl: string | null | undefined,
  eventLogoUrl: string | null | undefined,
  displayName: string,
): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const eventLogoHtml = eventLogoUrl
    ? `<img src="${esc(eventLogoUrl)}" class="logo" alt="" />`
    : '';
  const appLogoHtml = appLogoUrl ? `<img src="${esc(appLogoUrl)}" class="logo" alt="" />` : '';

  const rainHtml = timeline.rainPlanNotes
    ? `<div class="rain-plan"><div class="rain-label">Rain Plan</div><div class="rain-text">${esc(substituteTags(timeline.rainPlanNotes, eventTags))}</div></div>`
    : '';

  const sectionsHtml = sections
    .map((section) => {
      const entries = [...(section.entries ?? [])].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      );
      const rowsHtml =
        entries.length === 0
          ? `<tr><td colspan="3" class="empty">No entries</td></tr>`
          : entries
              .map((entry: TimelineEntry, idx: number) => {
                const locationHtml = entry.location
                  ? `<div class="location">@ ${esc(substituteTags(entry.location, eventTags))}</div>`
                  : '';
                const noteHtml = entry.vendorNote
                  ? `<td class="note">${esc(substituteTags(entry.vendorNote, eventTags))}</td>`
                  : '<td class="note"></td>';
                return (
                  `<tr class="${idx % 2 === 0 ? 'row-even' : 'row-odd'}">` +
                  `<td class="time">${esc(formatTime(entry.time))}</td>` +
                  `<td class="title"><span class="title-text">${esc(formatName(entry.title, eventTags))}</span>${locationHtml}</td>` +
                  noteHtml +
                  `</tr>`
                );
              })
              .join('');
      return (
        `<div class="section">` +
        `<div class="section-head">${esc(formatName(section.name, eventTags))}</div>` +
        `<table><tbody>${rowsHtml}</tbody></table>` +
        `</div>`
      );
    })
    .join('');

  const printedOn = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    `<!DOCTYPE html><html><head><meta charset="UTF-8">` +
    `<title>${esc(eventName)} — ${esc(displayName)}</title>` +
    `<style>
      *{print-color-adjust:exact;-webkit-print-color-adjust:exact;box-sizing:border-box;}
      @page{size:letter portrait;margin:15mm 15mm 15mm 15mm;}
      body{margin:0;padding:20px 30px;font-family:Georgia,serif;font-size:13px;color:#111;}
      .header{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #1f2937;}
      .logo{height:56px;width:auto;object-fit:contain;}
      .logo-center{display:flex;align-items:center;justify-content:center;flex:0 0 auto;}
      .logo-right{display:flex;align-items:center;justify-content:flex-end;}
      .event-info{text-align:left;}
      .event-name{font-size:22px;font-weight:700;color:#111;margin:0;}
      .timeline-name{font-size:16px;color:#374151;margin:4px 0 0;}
      .event-date{font-size:12px;color:#6b7280;margin:4px 0 0;}
      .rain-plan{margin-bottom:20px;padding:12px 16px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:4px;}
      .rain-label{font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px;}
      .rain-text{font-size:12px;color:#1e40af;}
      .section{margin-bottom:20px;}
      .section-head{background:#1f2937;color:#fff;padding:7px 14px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;}
      table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb;font-size:13px;}
      .row-even{background:#fff;}
      .row-odd{background:#f9fafb;}
      td{padding:7px 14px;vertical-align:top;}
      .time{font-family:monospace;color:#6d28d9;width:90px;white-space:nowrap;}
      .title-text{font-weight:600;}
      .location{font-size:11px;color:#6b7280;margin-top:2px;}
      .note{font-size:11px;color:#9ca3af;width:200px;}
      .empty{font-size:11px;color:#9ca3af;font-style:italic;}
      .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;text-align:center;}
    </style></head><body>` +
    `<div class="header">` +
    `<div class="event-info">` +
    `<h1 class="event-name">${esc(eventName)}</h1>` +
    `<div class="timeline-name">${esc(displayName)}</div>` +
    (timeline.eventDate
      ? `<div class="event-date">${esc(formatDisplayDate(timeline.eventDate))}</div>`
      : '') +
    `</div>` +
    `<div class="logo-center">${eventLogoHtml}</div>` +
    `<div class="logo-right">${appLogoHtml}</div>` +
    `</div>` +
    rainHtml +
    sectionsHtml +
    `<div class="footer">Printed ${esc(printedOn)}</div>` +
    `<script>window.onload=function(){setTimeout(function(){window.print();},400);}</` +
    `script>` +
    `</body></html>`
  );
}

export function TimelinePrintView({
  timeline,
  eventName,
  eventTags,
  appLogoUrl,
  eventLogoUrl,
  onClose,
}: TimelinePrintViewProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const sections = [...(timeline.sections ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );

  const displayName = timeline.title || timeline.templateName || 'Timeline';

  function handlePrint() {
    const printWin = window.open('', '_blank', 'width=900,height=700');
    if (!printWin) return;
    printWin.document.write(
      buildPrintHtml(
        timeline,
        sections,
        eventName,
        eventTags,
        appLogoUrl,
        eventLogoUrl,
        displayName,
      ),
    );
    printWin.document.close();
  }

  return (
    /* Screen-only preview overlay */
    <div className="fixed inset-0 z-50 bg-gray-100 overflow-y-auto">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          data-testid="print-close-button"
        >
          ← Back
        </button>
        <span className="text-sm text-gray-500">Print Preview</span>
        <div className="ml-auto">
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
            data-testid="print-button"
          >
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* Preview container */}
      <div className="flex justify-center py-8 px-4">
        <div className="bg-white shadow-xl w-full max-w-4xl" data-testid="print-content">
          <PrintContent
            timeline={timeline}
            sections={sections}
            eventName={eventName}
            eventTags={eventTags}
            appLogoUrl={appLogoUrl}
            eventLogoUrl={eventLogoUrl}
            displayName={displayName}
          />
        </div>
      </div>
    </div>
  );
}

interface PrintContentProps {
  timeline: Timeline;
  sections: TSection[];
  eventName: string;
  eventTags: EventTag[];
  appLogoUrl?: string | null;
  eventLogoUrl?: string | null;
  displayName: string;
}

function PrintContent({
  timeline,
  sections,
  eventName,
  eventTags,
  appLogoUrl,
  eventLogoUrl,
  displayName,
}: PrintContentProps) {
  return (
    <div className="p-10 font-sans text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Header: event info left | event logo center | app logo right */}
      <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-gray-800">
        <div className="text-left">
          <h1 className="text-2xl font-bold text-gray-900">{eventName}</h1>
          <h2 className="text-lg text-gray-700 mt-1">{displayName}</h2>
          {timeline.eventDate && (
            <p className="text-sm text-gray-500 mt-1">{formatDisplayDate(timeline.eventDate)}</p>
          )}
        </div>
        <div className="flex items-center justify-center">
          {eventLogoUrl && (
            <img
              src={eventLogoUrl}
              alt="Event logo"
              className="h-16 w-auto object-contain"
              data-testid="print-event-logo"
            />
          )}
        </div>
        <div className="flex items-center justify-end">
          {appLogoUrl && (
            <img
              src={appLogoUrl}
              alt="Program logo"
              className="h-16 w-auto object-contain"
              data-testid="print-app-logo"
            />
          )}
        </div>
      </div>

      {/* Rain plan */}
      {timeline.rainPlanNotes && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
            Rain Plan
          </p>
          <p className="text-sm text-blue-800">
            {substituteTags(timeline.rainPlanNotes, eventTags)}
          </p>
        </div>
      )}

      {/* Sections */}
      {sections.map((section) => {
        const entries = [...(section.entries ?? [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
        return (
          <div key={section.id} className="mb-6" data-testid={`print-section-${section.id}`}>
            <div className="bg-gray-800 text-white px-4 py-2 rounded-t">
              <h3 className="text-sm font-semibold uppercase tracking-wide">
                {formatName(section.name, eventTags)}
              </h3>
            </div>
            <table className="w-full border border-gray-200 rounded-b overflow-hidden text-sm">
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-2 text-gray-400 text-xs italic">
                      No entries
                    </td>
                  </tr>
                ) : (
                  entries.map((entry: TimelineEntry, idx: number) => (
                    <tr
                      key={entry.id}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      data-testid={`print-entry-${entry.id}`}
                    >
                      <td className="px-4 py-2 font-mono text-primary-700 w-20 shrink-0 whitespace-nowrap align-top">
                        {formatTime(entry.time)}
                      </td>
                      <td className="px-4 py-2 align-top">
                        <div className="font-medium">{formatName(entry.title, eventTags)}</div>
                        {entry.location && (
                          <div className="text-gray-500 text-xs mt-0.5">
                            @ {substituteTags(entry.location, eventTags)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs align-top">
                        {entry.vendorNote && substituteTags(entry.vendorNote, eventTags)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-400 text-center">
        Printed{' '}
        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
  );
}
